import { useEffect, useMemo, useState } from "react";
import { firestore } from "../shared/FirebaseConfig";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { useUser } from "../shared/UserContext";

// Local-date helper
function todayKeyLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Returns:
 *  - presentStudents: [{ id, studentName, timestamp }]
 *  - notPresentStudents: [{ id, studentName }]
 *  - loading, error
 *  - refreshFilteredStudents()
 *
 * Schema:
 *  - students: { enrolledClasses: [classId], studentName }
 *  - classes/{classId}/attendance/{YYYY-MM-DD}/students/{studentId}: { timestamp }
 */
export default function useFilteredStudents(classId) {
  const { schoolId } = useUser();
  const [roster, setRoster] = useState([]);
  const [presentMap, setPresentMap] = useState({});
  const [presentStudents, setPresentStudents] = useState([]);
  const [notPresentStudents, setNotPresentStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use local date key instead of UTC
  const todayKey = useMemo(() => todayKeyLocal(), []);

  const computeLists = (rosterIn = roster, presentIn = presentMap) => {
    const present = rosterIn
      .filter((s) => presentIn[s.id])
      .map((s) => ({
        id: s.id,
        studentName: s.studentName || "Unnamed Student",
        timestamp: presentIn[s.id].timestampMs ?? null,
        lunch: presentIn[s.id].lunch ?? null,
        grade: s.grade,
        gradYear: s.gradYear,
      }));

    const notPresent = rosterIn
      .filter((s) => !presentIn[s.id])
      .map((s) => ({
        id: s.id,
        studentName: s.studentName || "Unnamed Student",
        grade: s.grade,
        gradYear: s.gradYear,
      }));

    setPresentStudents(present);
    setNotPresentStudents(notPresent);
  };

  const refreshFilteredStudents = () => computeLists();

  // Roster listener
  useEffect(() => {
    setRoster([]);
    setPresentMap({});
    setPresentStudents([]);
    setNotPresentStudents([]);
    setError(null);
    setLoading(true);

    if (!classId || !schoolId) {
      setLoading(false);
      return;
    }

    const studentsCol = collection(firestore, "students");
    // schoolId scopes the query so the security rules can authorize it (same-school).
    const qRoster = query(
      studentsCol,
      where("schoolId", "==", schoolId),
      where("enrolledClasses", "array-contains", classId)
    );

    const unsub = onSnapshot(
      qRoster,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRoster(rows);
        setLoading(false);
      },
      (err) => {
        console.error("[useFilteredStudents] roster error:", err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [classId, schoolId]);

  // Attendance snapshot (local date key)
  useEffect(() => {
    if (!classId) return;

    const todayDoc = doc(firestore, "classes", classId, "attendance", todayKey);
    const studentsCol = collection(todayDoc, "students");

    const unsub = onSnapshot(
      studentsCol,
      (snap) => {
        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          const ts = data.timestamp;
          const timestampMs =
            ts && typeof ts.toMillis === "function"
              ? ts.toMillis()
              : ts || null;
          map[d.id] = { timestampMs, lunch: data.lunch || null };
        });
        setPresentMap(map);
      },
      (err) => {
        console.error("[useFilteredStudents] attendance error:", err);
        setError(err);
      }
    );

    return () => unsub();
  }, [classId, todayKey]);

  // Recompute lists whenever roster or presentMap changes
  useEffect(() => {
    computeLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, presentMap]);

  return {
    presentStudents,
    notPresentStudents,
    loading,
    error,
    refreshFilteredStudents,
  };
}
